import { reactivePick } from '@vueuse/shared';
import { type ExtractPropTypes, type PropType, computed } from 'vue';

import { withSizeProps, type SizePropType } from './size';
import type { IMaterialSymbolConfig } from '../../interfaces/materialSymbolConfig';
import { iconFamilyList } from '../../typings/iconFamilyType';
import { sizeList } from '../../typings/sizeType';
import { defaults } from '../defaults';

export function withIconProps(
  defaultIcon?: string,
  defaultIconSize?: SizePropType,
  defaultIconConfig = defaults.icons.config as IMaterialSymbolConfig
) {
  return {
    /**
     * Ikona
     */
    icon: {
      type: String,
      required: false,
      default: defaultIcon
    },

    /**
     * Wielkość ikony
     */
    iconSize: withSizeProps(defaultIconSize).size,

    /**
     * Rodzina ikony
     */
    iconFamily: {
      type: String,
      default: defaults.icons.family
    },

    /**
     * Klasa ikony
     */
    iconClass: { type: String },

    /**
     * Aria label ikony
     */
    iconLabel: { type: String },

    /**
     * Konfiguracja ikony
     */
    iconConfig: { type: Object as PropType<IMaterialSymbolConfig>, default: defaultIconConfig }
  };
}

/**
 * Propsy ikony
 *
 * @export
 * @param {*} [props={}]
 * @returns
 */
export function useIconProps(props: ExtractPropTypes<ReturnType<typeof withIconProps>>) {
  const componentProps = reactivePick(props, 'icon', 'iconSize', 'iconClass', 'iconLabel', 'iconFamily', 'iconConfig');

  const iconProps = computed(() => ({
    icon: componentProps.icon,
    class: componentProps.iconClass,
    size: componentProps.iconSize,
    label: componentProps.iconLabel,
    family: componentProps.iconFamily,
    config: componentProps.iconConfig
  }));

  return iconProps;
}

/**
 * Konfiguracja dla storybooka
 */
export const storiesIconArgs = {
  args: {
    icon: '',
    iconClass: '',
    iconFamily: defaults.icons.family,
    iconSize: undefined
  },

  argTypes: {
    icon: {
      control: { type: 'text' }
    },

    iconFamily: {
      options: iconFamilyList,
      control: { type: 'inline-radio' }
    },

    iconClass: {
      control: { type: 'text' }
    },

    iconSize: {
      options: sizeList,
      control: { type: 'inline-radio' }
    }
  }
};

/**
 * Konfiguracja dla styli ikony dla storybooka
 */
export const storiesIconConfigArgs = {
  args: {
    weight: defaults.icons.config.weight,
    grade: defaults.icons.config.grade,
    opticalSize: defaults.icons.config.opticalSize,
    fill: defaults.icons.config.fill
  },

  argTypes: {
    weight: {
      control: { type: 'range', min: 100, max: 700 }
    },

    grade: {
      control: { type: 'range', min: -25, max: 200 }
    },

    opticalSize: {
      control: { type: 'range', min: 1, max: 48 }
    },

    fill: {
      control: { type: 'boolean' }
    }
  }
};
